import type { Theme } from '../types/theme.types';
import { lightTheme } from './light-theme';

export const darkTheme: Theme = {
	...lightTheme,
	id: 'dark',
	name: 'dark',
	description: 'Default dark theme for SupplyMover',

	colors: {
		// CSS color values only - no Tailwind classes
		values: {
			// Core semantic colors - adjusted for dark backgrounds
			primary: '#60A5FA', // Lighter blue for dark mode
			primaryHover: '#3B82F6',
			primaryLight: '#1E40AF',
			primaryDark: '#DBEAFE',

			secondary: '#9CA3AF',
			secondaryHover: '#6B7280',
			secondaryLight: '#374151',

			success: '#34D399', // Lighter green for dark mode
			successHover: '#10B981',
			successLight: '#064E3B',

			warning: '#FBBF24', // Lighter amber for dark mode
			warningHover: '#F59E0B',
			warningLight: '#78350F',

			danger: '#F87171', // Lighter red for dark mode
			dangerHover: '#EF4444',
			dangerLight: '#7F1D1D',

			info: '#60A5FA',
			infoHover: '#3B82F6',
			infoLight: '#1E3A8A',

			// UI elements - inverted for dark mode
			background: '#111827',
			surface: '#1F2937',
			surfaceElevated: '#374151',
			container: '#1F2937',

			textPrimary: '#F9FAFB',
			textSecondary: '#9CA3AF',
			textTertiary: '#6B7280',
			textMuted: '#6B7280',
			textInverse: '#111827',

			borderLight: '#374151',
			borderMedium: '#4B5563',
			borderHeavy: '#6B7280',
			borderFocus: '#60A5FA',

			// Navigation specific
			navBackground: '#1F2937',
			navText: '#9CA3AF',
			navTextActive: '#FFFFFF',
			navHover: '#374151',
			navActive: '#374151',
			navIconDefault: '#6B7280',
			navIconActive: '#FFFFFF',
			navScrollbarTrack: '#111827',
			navScrollbarThumb: '#374151',
			navScrollbarThumbHover: '#4B5563',

			// General scrollbar colors (for non-nav scrollbars)
			scrollbarTrack: '#1F2937',
			scrollbarThumb: '#4B5563',
			scrollbarThumbHover: '#6B7280',

			// Chart colors - brighter for dark backgrounds
			chart1: '#60A5FA',
			chart2: '#34D399',
			chart3: '#FBBF24',
			chart4: '#A78BFA',
			chart5: '#F472B6',
			chart6: '#818CF8',

			// Chart trend colors (for sparklines and trend indicators)
			chartTrendUp: '#4ADE80',
			chartTrendDown: '#F87171',
			chartTrendNeutral: '#9CA3AF',

			// Icon colors
			iconPrimary: '#E5E7EB',
			iconSecondary: '#9CA3AF',
			iconInverse: '#111827',
			iconDanger: '#F87171',
			iconSuccess: '#34D399',
			iconWarning: '#FBBF24',
			iconInfo: '#60A5FA',

			// Navigation icon colors (numbered for reuse) - lighter for dark mode
			navIcon1: '#60A5FA', // Blue-400
			navIcon2: '#34D399', // Green-400
			navIcon3: '#FBBF24', // Amber-400
			navIcon4: '#A78BFA', // Purple-400
			navIcon5: '#F87171', // Red-400
			navIcon6: '#F472B6', // Pink-400
			navIcon7: '#818CF8', // Indigo-400
			navIcon8: '#2DD4BF', // Teal-400
			navIcon9: '#FB923C', // Orange-400
			navIcon10: '#38BDF8', // Sky-400
			navIcon11: '#C084FC', // Violet-400
			navIcon12: '#22D3EE', // Cyan-400
			navIcon13: '#A3E635', // Lime-400
			navIcon14: '#FACC15', // Yellow-400
			navIcon15: '#4ADE80', // Emerald-400
			navIcon16: '#FB7185', // Rose-400

			// Chip/pill colors (numbered for reuse) - adjusted for dark mode
			chipColor1: '#1E3A8A', // Blue 900
			chipColor2: '#14532D', // Green 900
			chipColor3: '#78350F', // Amber 900
			chipColor4: '#4C1D95', // Purple 900
			chipColor5: '#7F1D1D', // Red 900
			chipColor6: '#831843', // Pink 900
			chipColor7: '#312E81', // Indigo 900
			chipColor8: '#134E4A', // Teal 900
			chipColor9: '#7C2D12', // Orange 900
			chipColor10: '#0C4A6E', // Sky 900
			chipColor11: '#4C1D95', // Violet 900
			chipColor12: '#164E63', // Cyan 900
			chipColor13: '#365314', // Lime 900
			chipColor14: '#713F12', // Yellow 900
			chipColor15: '#064E3B', // Emerald 900
			chipColor16: '#881337', // Rose 900

			// Interactive backgrounds (with opacity) - adjusted for dark
			primaryBg: 'rgba(96, 165, 250, 0.2)',
			successBg: 'rgba(52, 211, 153, 0.2)',
			warningBg: 'rgba(202, 138, 4, 0.2)',
			dangerBg: 'rgba(248, 113, 113, 0.2)',
			infoBg: 'rgba(96, 165, 250, 0.2)',

			// Toast colors (for borders)
			toastSuccess: '#34d399',
			toastWarning: '#fbbf24',
			toastError: '#f87171',
			toastInfo: '#60a5fa',

			// Overlay
			overlay: 'rgba(0, 0, 0, 0.7)',
			shadow: 'rgba(0, 0, 0, 0.3)',

			// Focus ring colors
			focusRingPrimary: '#60A5FA',
			focusRingDanger: '#F87171',
			focusRingSuccess: '#34D399',
			focusRingWarning: '#FBBF24',
			focusRingDefault: '#9CA3AF',

			// Aura ring colors for AI agent interface (dark mode)
			auraUserSpeaking1: '#A78BFA', // Lighter purple for dark mode
			auraUserSpeaking2: '#C4B5FD',
			auraUserSpeaking3: '#DDD6FE',
			auraAgentSpeaking1: '#F59E0B', // Rich amber/gold start
			auraAgentSpeaking2: '#D97706', // Deep amber middle
			auraAgentSpeaking3: '#FCD34D', // Golden amber end
			auraIdle1: '#4B5563', // Darker gray for dark mode
			auraIdle2: '#6B7280',
			auraIdle3: '#9CA3AF',
			auraMuted1: '#F87171', // Lighter red for dark mode
			auraMuted2: '#FCA5A5',
			auraMuted3: '#FECACA',

			// Heat map gradient colors (light to dark green for dark mode)
			heatMapGreen1: '#064e3b', // Darkest green for dark mode
			heatMapGreen2: '#065f46',
			heatMapGreen3: '#047857',
			heatMapGreen4: '#059669',
			heatMapGreen5: '#10b981',
			heatMapGreen6: '#34d399',
			heatMapGreen7: '#6ee7b7',
			heatMapGreen8: '#a7f3d0',
			heatMapGreen9: '#d1fae5',
			heatMapGreen10: '#ecfdf5', // Lightest green for dark mode

			// Email template colors - adjusted for dark mode
			emailBackground: '#1F2937',
			emailContainerBackground: '#111827',
			emailTextPrimary: '#F9FAFB',
			emailTextSecondary: '#9CA3AF',
			emailLinkColor: '#60A5FA',
			emailButtonBackground: '#60A5FA',
			emailButtonText: '#111827',
			emailBorderColor: '#374151',
			emailHeaderBackground: '#111827',
			emailHeaderText: '#F9FAFB',
			emailFooterBackground: '#1F2937',
			emailFooterText: '#9CA3AF',

			// Automation flow node icon colors (adjusted for dark mode)
			automationStart: '#60A5FA', // Lighter blue for dark mode
			automationTemplate: '#60A5FA', // Lighter blue for dark mode
			automationCondition: '#FBBF24', // Brighter yellow/gold for dark mode
			automationConditionHeader: '#78350F', // Dark yellow/brown for condition header background
			automationDelay: '#9CA3AF', // Lighter gray for dark mode
			automationAction: '#34D399', // Lighter green for dark mode
			automationSplit: '#60A5FA', // Lighter blue for dark mode
			automationAgent: '#60A5FA', // Lighter blue for dark mode

			// React Flow automation designer colors (dark mode)
			automationNodeBg: '#1F2937',
			automationNodeBorder: '#374151',
			automationNodeText: '#F9FAFB',
			automationNodeHover: '#374151',
			automationNodeSelected: '#60A5FA',

			automationConnectionDefault: '#6B7280',
			automationConnectionHover: '#9CA3AF',
			automationConnectionTrue: '#34D399',
			automationConnectionFalse: '#F87171',
			automationConnectionSplitA: '#60A5FA',
			automationConnectionSplitB: '#A78BFA',

			automationHandleDefault: '#374151',
			automationHandleActive: '#60A5FA',
			automationHandleHover: '#9CA3AF',

			automationLabelBg: '#1F2937',
			automationLabelText: '#D1D5DB',
			automationLabelBorder: '#374151',

			// Automation node border colors (dark mode)
			automationStartBorder: '#3B82F6', // Blue for start nodes
			automationTemplateBorder: '#F59E0B', // Orange for template nodes
			automationConditionBorder: '#F59E0B', // Orange for condition nodes
			automationConditionIncompleteBorder: '#EF4444', // Bright red for incomplete conditions
			automationDelayBorder: '#F59E0B', // Orange for delay nodes
			automationActionBorder: '#F59E0B', // Orange for action nodes
			automationSplitBorder: '#F59E0B', // Orange for split nodes
			automationAgentBorder: '#F59E0B', // Orange for agent nodes
			automationDefaultBorder: '#F59E0B', // Orange default
			automationIncompleteBorder: '#F87171', // Light red for any incomplete node

			// Automation selection aura colors (dark mode)
			automationStartAura: '#3B82F6',
			automationTemplateAura: '#F59E0B',
			automationConditionAura: '#F59E0B',
			automationConditionIncompleteAura: '#EF4444',
			automationDelayAura: '#F59E0B',
			automationActionAura: '#F59E0B',
			automationSplitAura: '#F59E0B',
			automationAgentAura: '#F59E0B',
			automationDefaultAura: '#F59E0B',
			automationIncompleteAura: '#F87171',

			// Agent chat UI colors for proper contrast in dark mode
			agentMessageBg: '#374151', // Rich dark gray background for agent messages
			agentMessageText: '#F9FAFB', // White text for agent messages
			userMessageBg: '#3B82F6', // Same blue as light mode for consistency
			userMessageText: '#FFFFFF', // White text for better readability
			dangerButtonText: '#FFFFFF', // Always white text on danger buttons
			muted: '#9CA3AF', // Lighter gray for muted/disabled states in dark mode
		},

		// Tailwind classes are provided by ThemeService, not theme files
		classes: {} as any, // ThemeService will populate this

		// Logo paths
		logo: {
			primary: '/assets/supply-mover-logo-small-white.png',
			inverse: '/assets/supply-mover-logo-small.png',
		},
	},

	// Typography remains the same
	typography: lightTheme.typography,
	spacing: lightTheme.spacing,
	borderRadius: lightTheme.borderRadius,

	shadows: {
		// Adjusted shadows for dark mode
		xs: '0 1px 2px 0 rgb(0 0 0 / 0.1)',
		sm: '0 1px 3px 0 rgb(0 0 0 / 0.2)',
		base: '0 1px 3px 0 rgb(0 0 0 / 0.2)',
		md: '0 4px 6px -1px rgb(0 0 0 / 0.3)',
		lg: '0 10px 15px -3px rgb(0 0 0 / 0.3)',
		xl: '0 20px 25px -5px rgb(0 0 0 / 0.3)',
		'2xl': '0 25px 50px -12px rgb(0 0 0 / 0.5)',
		inner: 'inset 0 2px 4px 0 rgb(0 0 0 / 0.1)',
		none: 'none',
	},

	cssVars: {
		// Dark mode CSS variables
		'--color-primary': '#60A5FA',
		'--color-primary-hover': '#3B82F6',
		'--color-primary-light': '#1E40AF',
		'--color-primary-dark': '#DBEAFE',

		'--color-secondary': '#9CA3AF',
		'--color-secondary-hover': '#6B7280',
		'--color-secondary-light': '#374151',

		'--color-success': '#34D399',
		'--color-success-hover': '#10B981',
		'--color-success-light': '#064E3B',

		'--color-warning': '#FBBF24',
		'--color-warning-hover': '#F59E0B',
		'--color-warning-light': '#78350F',

		'--color-danger': '#F87171',
		'--color-danger-hover': '#EF4444',
		'--color-danger-light': '#7F1D1D',

		'--color-info': '#60A5FA',
		'--color-info-hover': '#3B82F6',
		'--color-info-light': '#1E3A8A',

		// UI colors
		'--color-background': '#111827',
		'--color-surface': '#1F2937',
		'--color-surface-elevated': '#374151',
		'--color-container': '#1F2937',

		'--color-text-primary': '#F9FAFB',
		'--color-text-secondary': '#9CA3AF',
		'--color-text-muted': '#6B7280',
		'--color-text-inverse': '#111827',

		'--color-border-light': '#374151',
		'--color-border-medium': '#4B5563',
		'--color-border-heavy': '#6B7280',
		'--color-border-focus': '#60A5FA',

		// Navigation
		'--color-nav-bg': '#1F2937',
		'--color-nav-text': '#9CA3AF',
		'--color-nav-text-active': '#FFFFFF',
		'--color-nav-hover': '#374151',
		'--color-nav-active': '#374151',
		'--color-nav-icon-default': '#6B7280',
		'--color-nav-icon-active': '#FFFFFF',
		'--color-nav-scrollbar-track': '#111827',
		'--color-nav-scrollbar-thumb': '#374151',
		'--color-nav-scrollbar-thumb-hover': '#4B5563',

		// General scrollbar colors
		'--color-scrollbar-track': '#1F2937',
		'--color-scrollbar-thumb': '#4B5563',
		'--color-scrollbar-thumb-hover': '#6B7280',

		// Interactive backgrounds
		'--color-interactive-primary': 'rgba(96, 165, 250, 0.2)',
		'--color-interactive-success': 'rgba(52, 211, 153, 0.2)',
		'--color-interactive-warning': 'rgba(251, 191, 36, 0.2)',
		'--color-interactive-danger': 'rgba(248, 113, 113, 0.2)',
		'--color-interactive-info': 'rgba(96, 165, 250, 0.2)',

		// Chart colors
		'--color-chart-1': '#60A5FA',
		'--color-chart-2': '#34D399',
		'--color-chart-3': '#FBBF24',
		'--color-chart-4': '#A78BFA',
		'--color-chart-5': '#F472B6',
		'--color-chart-6': '#818CF8',

		// Chart trend colors
		'--color-chart-trend-up': '#4ADE80',
		'--color-chart-trend-down': '#F87171',
		'--color-chart-trend-neutral': '#9CA3AF',

		// Icon colors
		'--color-icon-primary': '#E5E7EB',
		'--color-icon-secondary': '#9CA3AF',
		'--color-icon-inverse': '#111827',
		'--color-icon-danger': '#F87171',
		'--color-icon-success': '#34D399',
		'--color-icon-warning': '#FBBF24',
		'--color-icon-info': '#60A5FA',

		// Toast colors
		'--color-toast-success': '#34d399',
		'--color-toast-warning': '#fbbf24',
		'--color-toast-error': '#f87171',
		'--color-toast-info': '#60a5fa',

		// Overlay
		'--color-overlay': 'rgba(0, 0, 0, 0.7)',
		'--color-shadow': 'rgba(0, 0, 0, 0.3)',

		// Focus ring colors
		'--color-focus-ring-primary': '#60A5FA',
		'--color-focus-ring-danger': '#F87171',
		'--color-focus-ring-success': '#34D399',
		'--color-focus-ring-warning': '#FBBF24',
		'--color-focus-ring-default': '#9CA3AF',

		// Heat map gradient colors
		'--color-heat-map-green-1': '#064e3b',
		'--color-heat-map-green-2': '#065f46',
		'--color-heat-map-green-3': '#047857',
		'--color-heat-map-green-4': '#059669',
		'--color-heat-map-green-5': '#10b981',
		'--color-heat-map-green-6': '#34d399',
		'--color-heat-map-green-7': '#6ee7b7',
		'--color-heat-map-green-8': '#a7f3d0',
		'--color-heat-map-green-9': '#d1fae5',
		'--color-heat-map-green-10': '#ecfdf5',

		// Email template colors - dark mode
		'--color-email-background': '#1F2937',
		'--color-email-container-background': '#111827',
		'--color-email-text-primary': '#F9FAFB',
		'--color-email-text-secondary': '#9CA3AF',
		'--color-email-link': '#60A5FA',
		'--color-email-button-background': '#60A5FA',
		'--color-email-button-text': '#111827',
		'--color-email-border': '#374151',
		'--color-email-header-background': '#111827',
		'--color-email-header-text': '#F9FAFB',
		'--color-email-footer-background': '#1F2937',
		'--color-email-footer-text': '#9CA3AF',

		// Agent chat UI colors
		'--color-agent-message-bg': '#374151',
		'--color-agent-message-text': '#F9FAFB',
		'--color-user-message-bg': '#3B82F6',
		'--color-user-message-text': '#FFFFFF',
		'--color-danger-button-text': '#FFFFFF',

		// Typography (same as light)
		'--font-family-sans':
			'-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
		'--font-family-mono':
			'Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',

		'--font-size-xs': '0.75rem',
		'--font-size-sm': '0.875rem',
		'--font-size-base': '1rem',
		'--font-size-lg': '1.125rem',
		'--font-size-xl': '1.25rem',
		'--font-size-2xl': '1.5rem',
		'--font-size-3xl': '1.875rem',
		'--font-size-4xl': '2.25rem',
		'--font-size-5xl': '3rem',

		'--font-weight-light': '300',
		'--font-weight-normal': '400',
		'--font-weight-medium': '500',
		'--font-weight-semibold': '600',
		'--font-weight-bold': '700',

		'--line-height-tight': '1.25',
		'--line-height-normal': '1.5',
		'--line-height-relaxed': '1.625',

		// Spacing (same as light)
		'--spacing-xs': '0.25rem',
		'--spacing-sm': '0.5rem',
		'--spacing-md': '1rem',
		'--spacing-lg': '1.5rem',
		'--spacing-xl': '2rem',
		'--spacing-2xl': '3rem',
		'--spacing-3xl': '4rem',

		// Border radius (same as light)
		'--radius-none': '0',
		'--radius-sm': '0.125rem',
		'--radius-base': '0.25rem',
		'--radius-md': '0.375rem',
		'--radius-lg': '0.5rem',
		'--radius-xl': '0.75rem',
		'--radius-2xl': '1rem',
		'--radius-3xl': '1.5rem',
		'--radius-full': '9999px',

		// Shadows (adjusted for dark mode)
		'--shadow-xs': '0 1px 2px 0 rgb(0 0 0 / 0.1)',
		'--shadow-sm': '0 1px 3px 0 rgb(0 0 0 / 0.2)',
		'--shadow-base': '0 1px 3px 0 rgb(0 0 0 / 0.2)',
		'--shadow-md': '0 4px 6px -1px rgb(0 0 0 / 0.3)',
		'--shadow-lg': '0 10px 15px -3px rgb(0 0 0 / 0.3)',
		'--shadow-xl': '0 20px 25px -5px rgb(0 0 0 / 0.3)',
		'--shadow-2xl': '0 25px 50px -12px rgb(0 0 0 / 0.5)',
		'--shadow-inner': 'inset 0 2px 4px 0 rgb(0 0 0 / 0.1)',
		'--shadow-none': 'none',
	},

	// Metadata
	createdAt: '2024-01-01T00:00:00Z',
	updatedAt: '2024-01-01T00:00:00Z',
	author: 'SupplyMover Team',
};
